import { useMemo } from "react";
import {
  Box,
  makeStyles,
  Paper,
  Theme,
  Typography,
} from "@material-ui/core";
import { createTwoFilesPatch } from "diff";
import { Diff, Hunk, parseDiff } from "react-diff-view";
import { RecordDiffViewerProps } from "../../interface/RecordDiffViewerProps";

import "react-diff-view/style/index.css";

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    marginTop: theme.spacing(1.5),
    overflow: "hidden",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    boxShadow: "0 1px 0 rgba(27, 31, 36, 0.04)",
    backgroundColor: theme.palette.common.white,
    "& .diff": {
      width: "100%",
      borderCollapse: "collapse",
      tableLayout: "fixed",
      fontSize: 13,
    },
    "& .diff-gutter": {
      width: 56,
      minWidth: 56,
    },
    "& .diff-code": {
      whiteSpace: "pre-wrap",
      wordBreak: "break-word",
    },
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.25, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.grey[100],
  },
  title: {
    fontSize: 12,
    fontWeight: 600,
    lineHeight: "16px",
    color: theme.palette.text.secondary,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },
  newTitle: {
    color: theme.palette.success.dark,
  },
  emptyState: {
    padding: theme.spacing(2),
    color: theme.palette.text.secondary,
  },
}));

const ensureTrailingNewline = (value: string) =>
  value.endsWith("\n") ? value : `${value}\n`;

const buildGitPatch = (
  oldTitle: string,
  newTitle: string,
  oldValue: string,
  newValue: string
) => {
  const oldPath = `a/${oldTitle}`;
  const newPath = `b/${newTitle}`;
  const patchBody = createTwoFilesPatch(
    oldPath,
    newPath,
    oldValue,
    newValue,
    "",
    "",
    { context: 3 }
  ).replace(/^=+\r?\n/, "");

  return `diff --git ${oldPath} ${newPath}\n${patchBody}`;
};

const formatDiffValue = (value: string) => {
  if (!value) {
    return "";
  }

  try {
    return ensureTrailingNewline(JSON.stringify(JSON.parse(value), null, 2));
  } catch {
    return ensureTrailingNewline(value);
  }
};

const RecordDiffViewer = ({
  oldValue,
  newValue,
  oldTitle = "Before",
  newTitle = "After",
  splitView = true,
}: RecordDiffViewerProps) => {
  const classes = useStyles();

  const diffFile = useMemo(() => {
    try {
      const patch = buildGitPatch(
        oldTitle,
        newTitle,
        formatDiffValue(oldValue),
        formatDiffValue(newValue)
      );

      return parseDiff(patch, { nearbySequences: "zip" })[0];
    } catch {
    return undefined;
    }
  }, [newTitle, newValue, oldTitle, oldValue]);

  if (!diffFile || !diffFile.hunks.length) {
    return (
      <Paper className={classes.container} elevation={0}>
        <Box className={classes.header}>
          <Typography className={classes.title}>{oldTitle}</Typography>
          <Typography className={`${classes.title} ${classes.newTitle}`}>
            {newTitle}
          </Typography>
        </Box>
        <Typography className={classes.emptyState}>
          No differences recorded.
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper className={classes.container} elevation={0}>
      <Box className={classes.header}>
        <Typography className={classes.title}>{oldTitle}</Typography>
        <Typography className={`${classes.title} ${classes.newTitle}`}>
          {newTitle}
        </Typography>
      </Box>
      <Diff
        viewType={splitView ? "split" : "unified"}
        diffType={diffFile.type}
        hunks={diffFile.hunks}
        optimizeSelection
      >
        {(hunks) => hunks.map((hunk) => <Hunk key={hunk.content} hunk={hunk} />)}
      </Diff>
    </Paper>
  );
};

export default RecordDiffViewer;
